import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, MessageSquare, Mail, Plus, Edit, Trash2, Loader2, Info, Eye } from 'lucide-react';
import { toast } from 'sonner';

const eventLabels = {
  order_created: 'Pesanan Baru',
  payment_success: 'Pembayaran Berhasil',
  payment_failed: 'Pembayaran Gagal',
  order_shipped: 'Pesanan Dikirim',
  order_delivered: 'Pesanan Diterima',
  order_canceled: 'Pesanan Dibatalkan',
  membership_active: 'Membership Aktif',
  course_enrolled: 'Pendaftaran Kursus',
  donation_received: 'Donasi Diterima',
  donation_completed: 'Donasi Berhasil',
  affiliate_commission: 'Komisi Affiliate',
  termin_reminder: 'Pengingat Termin',
  termin_overdue: 'Termin Jatuh Tempo',
  cod_confirmed: 'COD Dikonfirmasi',
  low_stock: 'Stok Rendah',
  cart_abandoned: 'Keranjang Ditinggalkan',
  unpaid_reminder: 'Pengingat Pembayaran',
  customer_registered: 'Pelanggan Baru',
  checkout_new_account: 'Akun Otomatis Checkout',
  ppob_order_created: 'Pesanan PPOB Baru',
  ppob_order_completed: 'PPOB Berhasil (SN)',
  webinar_registered: 'Pendaftaran Webinar',
  webinar_payment_confirmed: 'Pembayaran Webinar',
  webinar_reminder: 'Reminder Webinar',
  webinar_completed: 'Webinar Selesai',
  webinar_recording: 'Rekaman Webinar',
  event_registered: 'Pendaftaran Event',
  event_payment_confirmed: 'Pembayaran Tiket Event',
  event_reminder: 'Reminder Event',
  event_checkin: 'Check-in Event',
};

const defaultForm = { event: 'order_created', channel: 'whatsapp', subject: '', message: '', is_active: true, variables: [] };

export default function NotificationTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [activeChannel, setActiveChannel] = useState('whatsapp');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetch = async () => {
    try {
      const r = await api.get('/tenant/notification-templates');
      setTemplates(r.data.templates || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    if (!form.message.trim()) return toast.error('Pesan tidak boleh kosong');
    setSaving(true);
    try {
      const variables = (form.message.match(/\{(\w+)\}/g) || []).map(v => v.slice(1, -1));
      const payload = { ...form, variables };
      if (editing) {
        await api.put(`/tenant/notification-templates/${editing}`, payload);
      } else {
        await api.post('/tenant/notification-templates', payload);
      }
      setShowDialog(false);
      setEditing(null);
      setForm(defaultForm);
      fetch();
      toast.success('Template disimpan');
    } catch (e) {
      toast.error('Gagal menyimpan template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus template ini?')) return;
    try {
      await api.delete(`/tenant/notification-templates/${id}`);
      fetch();
      toast.success('Template dihapus');
    } catch (e) {
      toast.error('Gagal menghapus template');
    }
  };

  const handleToggle = async (template) => {
    try {
      await api.put(`/tenant/notification-templates/${template.id}`, { ...template, is_active: !template.is_active });
      fetch();
    } catch (e) {
      toast.error('Gagal mengubah status');
    }
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm(t);
    setShowDialog(true);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...defaultForm, channel: activeChannel });
    setShowDialog(true);
  };

  const filteredTemplates = templates.filter(t => t.channel === activeChannel);

  const handlePreview = async (template) => {
    setLoadingPreview(true);
    try {
      const r = await api.post('/tenant/notification-templates/preview-email', {
        event: template.event,
        subject: template.subject || '',
        message: template.message || '',
      });
      setPreviewHtml(r.data.html);
      setShowPreview(true);
    } catch (e) {
      toast.error('Gagal memuat preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div data-testid="notification-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Template Notifikasi</h1>
          <p className="text-sm text-slate-500">Kelola template pesan otomatis untuk pelanggan</p>
        </div>
        <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-template-btn">
          <Plus size={16} className="mr-2" /> Tambah Template
        </Button>
      </div>

      <Tabs value={activeChannel} onValueChange={setActiveChannel}>
        <TabsList className="mb-6">
          <TabsTrigger value="whatsapp" className="gap-2"><MessageSquare size={14} /> WhatsApp</TabsTrigger>
          <TabsTrigger value="email" className="gap-2"><Mail size={14} /> Email</TabsTrigger>
        </TabsList>

        <TabsContent value="whatsapp">
          <TemplateList templates={filteredTemplates} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggle} channel="whatsapp" />
        </TabsContent>
        <TabsContent value="email">
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 flex items-center gap-2"><Info size={14} /> Email akan dikirim dalam format HTML profesional dengan header gradient, detail tabel, tombol CTA, dan branding toko. Anda cukup atur <strong>subject</strong> dan <strong>pesan isi</strong> — desain cantik otomatis!</p>
          </div>
          <TemplateList templates={filteredTemplates} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggle} onPreview={handlePreview} channel="email" />
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Template' : 'Tambah Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Trigger</Label>
                <Select value={form.event} onValueChange={(v) => setForm({ ...form, event: v })}>
                  <SelectTrigger data-testid="event-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(eventLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.channel === 'email' && (
              <div>
                <Label>Subject Email</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Pesanan Baru #{order_number}" data-testid="subject-input" />
              </div>
            )}
            <div>
              <Label>{form.channel === 'email' ? 'Isi Pesan Email' : 'Pesan Template'}</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={form.channel === 'email' ? 3 : 6} placeholder={form.channel === 'email' ? 'Terima kasih telah berbelanja! Pesanan Anda telah berhasil dibuat.' : 'Halo {customer_name}, pesanan #{order_number}...'} data-testid="message-input" />
              {form.channel === 'email' && (
                <p className="text-xs text-slate-500 mt-1">Tips: Untuk email, cukup tulis isi pesan singkat. Header, detail pesanan, dan desain cantik akan otomatis ditambahkan.</p>
              )}
              <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1"><Info size={12} /> Variabel yang tersedia:</p>
                <div className="flex flex-wrap gap-1">
                  {['customer_name', 'order_number', 'total', 'product_name', 'customer_number', 'sn', 'tripay_message', 'plan_name', 'course_title', 'campaign_title', 'amount', 'termin_number', 'due_date', 'webinar_title', 'speaker_name', 'scheduled_at', 'meeting_url', 'replay_url', 'certificate_url'].map(v => (
                    <Badge key={v} variant="outline" className="text-xs cursor-pointer hover:bg-slate-100" onClick={() => setForm({ ...form, message: form.message + `{${v}}` })}>
                      {`{${v}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Aktifkan template ini</Label>
            </div>
            <div className="flex gap-2">
              {form.channel === 'email' && (
                <Button type="button" variant="outline" onClick={() => handlePreview(form)} disabled={loadingPreview} className="flex-1">
                  {loadingPreview ? <Loader2 size={14} className="animate-spin mr-2" /> : <Eye size={14} className="mr-2" />}
                  Preview Email
                </Button>
              )}
              <Button onClick={handleSave} className={`bg-[#2D6CDF] hover:bg-[#1A5BC4] ${form.channel === 'email' ? 'flex-1' : 'w-full'}`} disabled={saving} data-testid="save-template-btn">
                {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                Simpan Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={16} /> Preview Email</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden bg-gray-100">
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full border-0"
              style={{ minHeight: '500px' }}
              sandbox=""
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateList({ templates, onEdit, onDelete, onToggle, onPreview, channel }) {
  if (!templates.length) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="py-12 text-center text-slate-400">
          Belum ada template untuk channel ini
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {templates.map(t => (
        <Card key={t.id} className={`border-slate-200 shadow-sm ${!t.is_active ? 'opacity-60' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${t.channel === 'whatsapp' ? 'bg-emerald-100' : 'bg-violet-100'}`}>
                  {t.channel === 'whatsapp' ? <MessageSquare size={18} className="text-emerald-600" /> : <Mail size={18} className="text-violet-600" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-900">{eventLabels[t.event] || t.event}</h3>
                    <Badge variant="outline" className={t.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                      {t.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </div>
                  {t.subject && <p className="text-sm text-slate-600 mt-1">Subject: {t.subject}</p>}
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{t.message}</p>
                  {t.variables?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {t.variables.map(v => (
                        <Badge key={v} variant="outline" className="text-xs">{`{${v}}`}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Switch checked={t.is_active} onCheckedChange={() => onToggle(t)} />
                {channel === 'email' && onPreview && (
                  <Button variant="ghost" size="icon" onClick={() => onPreview(t)} title="Preview Email">
                    <Eye size={14} className="text-blue-500" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => onEdit(t)} data-testid={`edit-template-${t.id}`}>
                  <Edit size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onDelete(t.id)} data-testid={`delete-template-${t.id}`}>
                  <Trash2 size={14} className="text-red-500" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
